import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Building2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import ConsumerLayout from '@/components/ConsumerLayout';
import api from '@/lib/api';

const STATE_NAMES = { co: 'Colorado', ca: 'California', tx: 'Texas', ny: 'New York', fl: 'Florida', az: 'Arizona', wa: 'Washington' };

export default function StateBrowse({ overrideState, contentType: propContentType }) {
  const params = useParams();
  const state = overrideState || params.state;
  const contentType = propContentType || params.contentType || 'venues';
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = require('react-router-dom').useLocation();
  const basePath = contentType || (location.pathname.includes('/experiences') ? 'experiences' : location.pathname.includes('/events') ? 'events' : 'venues');
  const stateName = STATE_NAMES[state?.toLowerCase()] || state?.toUpperCase();

  useEffect(() => {
    api.get('/discover/cities').then(res => {
      const filtered = (res.data.cities || []).filter(c => c.state?.toLowerCase() === state?.toLowerCase());
      setCities(filtered);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [state]);

  return (
    <ConsumerLayout darkTheme>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-6">
          <Link to={`/${basePath}`} className="hover:text-white">{basePath === 'venues' ? 'Venues' : basePath === 'experiences' ? 'Experiences' : 'Events'}</Link>
          <span>/</span>
          <span className="text-white">{stateName}</span>
        </div>

        <h1 className="text-4xl font-bold text-white mb-2">{stateName} {basePath === 'venues' ? 'Venues' : basePath === 'experiences' ? 'Experiences' : 'Events'}</h1>
        <p className="text-slate-400 mb-8">{cities.length} {cities.length === 1 ? 'city' : 'cities'} with EVENT★GOERS partners</p>

        {loading ? <div className="text-center py-20 text-slate-400">Loading...</div> : cities.length === 0 ? (
          <div className="text-center py-20"><Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" /><p className="text-slate-400">No partners in {stateName} yet.</p></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cities.map(city => (
              <Link key={city.slug} to={`/${basePath}/${state}/${city.slug}`}>
                <Card className="bg-slate-800/50 border-slate-700 hover:border-indigo-500 transition-all p-6 h-full">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-indigo-600/20 flex items-center justify-center"><MapPin className="w-6 h-6 text-indigo-400" /></div>
                    <div>
                      <h3 className="font-bold text-white text-xl">{city.city}</h3>
                      <p className="text-sm text-slate-400">{city.display}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="secondary">{city.venue_count} venue{city.venue_count !== 1 ? 's' : ''}</Badge>
                    {city.neighborhoods?.length > 0 && <Badge variant="outline" className="text-slate-400">{city.neighborhoods.length} neighborhood{city.neighborhoods.length !== 1 ? 's' : ''}</Badge>}
                  </div>
                  {city.neighborhoods?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">{city.neighborhoods.slice(0, 5).map(n => <span key={n} className="text-xs text-slate-500">{n}</span>)}</div>
                  )}
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </ConsumerLayout>
  );
}
